import { useEffect, useRef, useState, useCallback } from 'react';
import forcePalmSprite from '../Moves/PRAS- Force Palm.png';
import './ForcePalmAnimation.css';

/**
 * PROFESSIONAL FORCE PALM ANIMATION
 * Based on Karate Chop animation logic and Pokémon Emerald reference analysis
 * 
 * Key Features:
 * - Uses 4-frame sprite system (768x192, 4 frames of 192x192 each)
 * - Professional sliding palm strike with energy effects
 * - Fighting-type themed with enhanced impact visuals
 * - Gaming company quality with layered effects and smooth transitions
 */
export default function ForcePalmAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  const [animationPhase, setAnimationPhase] = useState('idle');
  
  // Animation phase transition hook
  const transitionTo = useCallback((phase, delay = 0) => {
    setTimeout(() => setAnimationPhase(phase), delay);
  }, []);
  
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container
    const container = playerEl.closest('.battle-container');
    if (!container) {
      console.error('Battle container not found');
      onComplete();
      return;
    }

    // Get positions for opponent (target)
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // SPRITE CONFIGURATION - Force Palm (768x192, 4 frames of 192x192 each)
    const SPRITE_CONFIG = {
      frameWidth: 192,     // Width of each frame (192x192)
      frameHeight: 192,    // Height of sprite
      prepFrame: 0,        // frameIndex=0 (preparation palm)
      impactFrame: 1,      // frameIndex=1 (impact palm strike)
      energyFrame: 2,      // frameIndex=2 (energy burst)
      peakFrame: 3,        // frameIndex=3 (peak energy effect)
    };

    // Calculate perfect center positioning over target
    const centerX = o.left - containerRect.left + o.width / 2;
    const centerY = o.top - containerRect.top + o.height / 2;

    // TIMING CONFIGURATION - Professional gaming company timing
    const TIMING = {
      preparation: 150,    // Player stance and initial palm preparation
      slideIn: 120,        // Palm sliding into position
      palmImpact: 220,     // frameIndex=1 duration (palm strike impact)
      energyBurst: 200,    // frameIndex=2 duration (energy burst)
      peakEnergy: 280,     // frameIndex=3 duration (peak energy effect)
      fadeOut: 200,        // Clean fade out
      total: 1170,         // Total animation duration
    };

    console.log('🤚 Force Palm Animation - Professional Version Starting');
    
    // PHASE 1: PREPARATION (0-150ms)
    // Player fighting stance preparation with initial palm energy
    setAnimationPhase('preparation');
    playerEl.style.transition = 'transform 150ms ease-in-out';
    playerEl.style.transform = 'scale(1.08) rotate(-3deg)';
    
    // Subtle target highlight preparation
    opponentEl.style.transition = 'filter 150ms ease-in';
    opponentEl.style.filter = 'brightness(1.15)';
    
    // Create initial palm preparation effect - frameIndex=0
    const prepTimer = setTimeout(() => {
      console.log('🤚 Phase 1.5: Palm Preparation Effect (frameIndex=0)');
      
      const prepEffect = document.createElement('div');
      prepEffect.className = 'force-palm-preparation';
      animRef.current = prepEffect;
      
      // Position for preparation phase
      const prepX = centerX - SPRITE_CONFIG.frameWidth / 2 - 100; // Start further left
      const prepY = centerY - SPRITE_CONFIG.frameHeight / 2;
      
      prepEffect.style.left = `${prepX}px`;
      prepEffect.style.top = `${prepY}px`;
      prepEffect.style.width = `${SPRITE_CONFIG.frameWidth}px`;
      prepEffect.style.height = `${SPRITE_CONFIG.frameHeight}px`;
      
      // Set to frameIndex=0 (preparation palm)
      prepEffect.style.backgroundImage = `url(${forcePalmSprite})`;
      prepEffect.style.backgroundSize = '768px 192px'; // Full sprite sheet
      prepEffect.style.backgroundPosition = '0px 0px'; // frameIndex=0 (first 192px)
      
      container.appendChild(prepEffect);
      
    }, 50);
    
    // PHASE 2: SLIDING PALM IMPACT - frameIndex=1 with motion (150-490ms)
    const palmImpactTimer = setTimeout(() => {
      setAnimationPhase('palm_impact');
      console.log('🤚 Phase 2: Sliding Palm Impact (frameIndex=1)');
      
      if (animRef.current) {
        // Transition to frameIndex=1 (impact palm strike)
        animRef.current.style.backgroundPosition = '-192px 0px'; // frameIndex=1 (second 192px frame)
        animRef.current.className = 'force-palm-impact';
        
        // Start dramatic slide motion from left to right
        const startX = centerX - SPRITE_CONFIG.frameWidth / 2 - 90; // Start 90px left
        const endX = centerX - SPRITE_CONFIG.frameWidth / 2 + 50;   // End 50px right
        
        animRef.current.style.left = `${startX}px`;
        
        // Animate dramatic sliding motion across target
        setTimeout(() => {
          if (animRef.current) {
            animRef.current.style.left = `${endX}px`;
            console.log(`🤚 Sliding from ${startX}px to ${endX}px (${endX - startX}px travel)`);
          }
        }, 40); // Slight delay for dramatic slide start
        
        // Impact flash effect at strike center
        setTimeout(() => {
          const flashEffect = document.createElement('div');
          flashEffect.className = 'force-palm-flash';
          flashEffect.style.left = `${centerX}px`;
          flashEffect.style.top = `${centerY}px`;
          container.appendChild(flashEffect);
          
          // Clean up flash after animation
          setTimeout(() => {
            if (flashEffect.parentNode) {
              flashEffect.parentNode.removeChild(flashEffect);
            }
          }, 250);
        }, TIMING.slideIn + 30); // Flash during slide completion
      }
      
    }, TIMING.preparation);
    
    // PHASE 3: ENERGY BURST - frameIndex=2 (490-690ms)
    const energyBurstTimer = setTimeout(() => {
      setAnimationPhase('energy_burst');
      console.log('🤚 Phase 3: Energy Burst (frameIndex=2)');
      
      if (animRef.current) {
        // Transition to frameIndex=2 (energy burst)
        animRef.current.style.backgroundPosition = '-384px 0px'; // frameIndex=2 (third 192px frame)
        animRef.current.className = 'force-palm-energy-burst';
        animRef.current.style.transition = 'none'; // Remove slide transition
        
        // Center the energy burst for maximum impact
        const centerXPos = centerX - SPRITE_CONFIG.frameWidth / 2;
        animRef.current.style.left = `${centerXPos}px`;
        
        console.log('🤚 Switched to frameIndex=2 (energy burst) at position -384px');
        
        // Opponent hit reaction starts
        opponentEl.classList.add('force-palm-hit');
        
        // Create energy particles effect
        setTimeout(() => {
          const particlesEffect = document.createElement('div');
          particlesEffect.className = 'force-palm-particles';
          particlesEffect.style.left = `${centerX}px`;
          particlesEffect.style.top = `${centerY}px`;
          container.appendChild(particlesEffect);
          
          // Clean up particles after animation
          setTimeout(() => {
            if (particlesEffect.parentNode) {
              particlesEffect.parentNode.removeChild(particlesEffect);
            }
          }, 400);
        }, 50);
      }
      
    }, TIMING.preparation + TIMING.slideIn + TIMING.palmImpact);
    
    // PHASE 4: PEAK ENERGY - frameIndex=3 (690-970ms)
    const peakEnergyTimer = setTimeout(() => {
      setAnimationPhase('peak_energy');
      console.log('🤚 Phase 4: Peak Energy (frameIndex=3)');
      
      if (animRef.current) {
        // Transition to frameIndex=3 (peak energy effect)
        animRef.current.style.backgroundPosition = '-576px 0px'; // frameIndex=3 (fourth 192px frame)
        animRef.current.className = 'force-palm-peak-energy';
        
        console.log('🤚 Switched to frameIndex=3 (peak energy) at position -576px');
        
        // Enhanced screen tint for peak energy
        const screenTint = document.createElement('div');
        screenTint.className = 'force-palm-screen-tint';
        container.appendChild(screenTint);
        
        // Clean up screen tint
        setTimeout(() => {
          if (screenTint.parentNode) {
            screenTint.parentNode.removeChild(screenTint);
          }
        }, TIMING.peakEnergy);
      }
      
    }, TIMING.preparation + TIMING.slideIn + TIMING.palmImpact + TIMING.energyBurst);
    
    // PHASE 5: FADE OUT (970-1170ms)
    const fadeOutTimer = setTimeout(() => {
      setAnimationPhase('fade_out');
      console.log('🤚 Phase 5: Fade Out');
      
      if (animRef.current) {
        animRef.current.className = 'force-palm-fade-out';
      }
      
      // Reset opponent
      opponentEl.style.filter = '';
      opponentEl.classList.remove('force-palm-hit');
      
    }, TIMING.preparation + TIMING.slideIn + TIMING.palmImpact + TIMING.energyBurst + TIMING.peakEnergy);
    
    // PHASE 6: COMPLETE (1170ms)
    const completeTimer = setTimeout(() => {
      setAnimationPhase('complete');
      console.log('🤚 Phase 6: Animation Complete');
      
      // Reset player position
      playerEl.style.transition = 'transform 200ms ease-out';
      playerEl.style.transform = '';
      
      // Clean up palm effect
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Animation complete callback
      setTimeout(() => {
        onComplete();
      }, 100);
      
    }, TIMING.total);

    // Enhanced cleanup function
    return () => {
      clearTimeout(prepTimer);
      clearTimeout(palmImpactTimer);
      clearTimeout(energyBurstTimer);
      clearTimeout(peakEnergyTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(completeTimer);
      
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Reset states
      if (playerEl) {
        playerEl.style.transition = '';
        playerEl.style.transform = '';
      }
      if (opponentEl) {
        opponentEl.style.transition = '';
        opponentEl.style.filter = '';
        opponentEl.classList.remove('force-palm-hit');
      }
      
      console.log('🤚 Force Palm Animation: Cleanup completed');
    };
  }, [playerRef, opponentRef, onComplete, transitionTo]);

  return null;
}

// Set the move type for proper coloring in the battle UI
ForcePalmAnimation.moveType = "fighting";
